import type { GeoPoint } from '../dto/geo.dto.js'
import {
  isPointOnRingEdge,
  isPointStrictlyInRing,
} from '../validators/polygon.validator.js'

/**
 * One area a tree might fall in.
 *
 * The caller passes them in the order they were created. A tree that lands on
 * a fence two areas share is given to the earlier one, so the same tree does
 * not change areas because a list came back in a different order.
 */
export interface AreaRing {
  id: string
  name: string
  vertices: GeoPoint[]
}

/**
 * The area that contains a point, or null when it sits in none of them.
 *
 * A point strictly inside a ring belongs to that ring. Areas are not allowed
 * to overlap, so there is at most one such ring; if bad data has two, the
 * earlier one wins.
 *
 * A point exactly on an edge is not strictly inside anything — `isPointInRing`
 * will not commit either way — so it is given to the earliest area whose
 * edge it lies on. That is the fence two tenants share, and the assignment
 * has to be the same every time the proposal is built.
 */
export function areaForPoint(
  point: GeoPoint,
  areas: readonly AreaRing[],
): AreaRing | null {
  let onEdge: AreaRing | null = null
  for (const area of areas) {
    if (area.vertices.length < 3) continue
    if (isPointOnRingEdge(point, area.vertices)) {
      onEdge ??= area
      continue
    }
    if (isPointStrictlyInRing(point, area.vertices)) return area
  }
  return onEdge
}

/** The label on a remainder row when a site is itemized and some trees are in no area. */
export const REST_OF_SITE_LABEL = 'Rest of site'

/**
 * The pricing-row label for one slice of a site.
 *
 * A site that is not itemized keeps its own name. An area is billed under
 * "Front Entrance — Common Area". Trees in no area, and only when the site
 * does have areas, are "Front Entrance — Rest of site".
 */
export function areaRowLabel(
  siteName: string,
  areaName: string | null,
  split: boolean,
): string {
  if (!split) return siteName
  if (areaName === null || areaName === '') {
    return `${siteName} — ${REST_OF_SITE_LABEL}`
  }
  return `${siteName} — ${areaName}`
}

/** One pricing slice: a whole site, one area name, or the trees in no area. */
export interface AreaGroup<T> {
  areaName: string | null
  displayName: string
  /** True when this row is one slice of an itemized site, including the remainder. */
  split: boolean
  items: T[]
}

/**
 * Break a site's trees into the rows a bill would show.
 *
 * Rings that share a name add up to one row: the name is what gets billed,
 * and two courtyards both called "Common Area" are one line. The remainder
 * row exists only when some trees sit in no area. A site with no areas stays
 * one row under the site's name, even when itemizing is on — "Rest of site"
 * would be naming a split that did not happen.
 *
 * `areaNamesInOrder` is the distinct names in the order the rings were
 * created, so an empty area still appears and two renders of the same site
 * list the rows the same way.
 */
export function groupByAreaName<
  T extends { areaName?: string | null | undefined },
>(
  siteName: string,
  items: readonly T[],
  areaNamesInOrder: readonly string[],
  itemize: boolean,
): AreaGroup<T>[] {
  const namedOnItems = items.some((item) => {
    const name = item.areaName
    return name !== null && name !== undefined && name !== ''
  })
  if (!itemize || (areaNamesInOrder.length === 0 && !namedOnItems)) {
    return [
      {
        areaName: null,
        displayName: siteName,
        split: false,
        items: [...items],
      },
    ]
  }

  const buckets = new Map<string, T[]>()
  const order: string[] = []
  const seen = new Set<string>()
  for (const name of areaNamesInOrder) {
    if (name === '' || seen.has(name)) continue
    seen.add(name)
    order.push(name)
    buckets.set(name, [])
  }

  const remainder: T[] = []
  for (const item of items) {
    const name = item.areaName
    if (name === null || name === undefined || name === '') {
      remainder.push(item)
      continue
    }
    const list = buckets.get(name)
    if (list === undefined) {
      buckets.set(name, [item])
      order.push(name)
    } else {
      list.push(item)
    }
  }

  const groups: AreaGroup<T>[] = order.map((name) => ({
    areaName: name,
    displayName: areaRowLabel(siteName, name, true),
    split: true,
    items: buckets.get(name) ?? [],
  }))
  if (remainder.length > 0) {
    groups.push({
      areaName: null,
      displayName: areaRowLabel(siteName, null, true),
      split: true,
      items: remainder,
    })
  }
  return groups
}
